import { v4 as uuidv4 } from 'uuid';

import * as constants from '../constants/index';
import { TypeContainer, Types } from '../actions/typeContainer';
import {
  SchemaBasketData,
  SchemaBasketInfo,
  SchemaIndustry,
  SchemaLocationItem,
  SchemaPaymentmethods,
  SchemaPossibleBookingDates,
  SchemaProduct,
  SchemaSearchCompletion,
  SchemaSelectFilter,
  SchemaAvailableProductGroupAttributes,
  SchemaProductGroupNode,
} from '@repo/legacy-commerce-api';
import { BasketActionType, basketReducer } from './basket';
import { ProductActionType, productReducer } from './product';
import { LocationsActionType, locationsReducer } from './locations';
import { LoadingErrorNoticeActionType, loadingErrorNoticeReducer } from './errorNotice';
import { AgencyActionType, agencyReducer } from './agency';
import { ecommerceSchemaReducer, EcomSchemaActionType } from './industry';
import { resultFilterReducer, ResultFiltersActionType } from './resultFilters';
import { AppUtilityActionType, appUtilitySchemaReducer } from './appUtilityState';
import { SujetActionType, sujetReducer } from './sujet';
import { Journey1LegacyActionType, journey1LegacyReducer } from './journey1Legacy';
import { CheckoutFastbookActionType, checkoutFastbookReducer } from './checkoutFastbook';

/**
 * The reality is we can't completely type this out, add a type here when you know it and it will spread:
 */
type UserDataEC = {
  clientHash: string | null;
  basketData: SchemaBasketData | null;
  basketInfo: (SchemaBasketInfo & { [key: string]: any }) | null; // Open Interface idea, passthrough to accept additions
  lastBookedBasketInfo: (SchemaBasketInfo & { [key: string]: any }) | null;
  mapCenter: { lat: number; lng: number };
  basketProducts: {
    products: SchemaProduct[];
    productsById: Record<string, SchemaProduct>;
    productsByUrn: Record<string, SchemaProduct>;
    productsByUrnDate: Record<string, SchemaProduct>;
    productsByUrnAll: Record<string, SchemaProduct[]>;
    stats: Record<string, never> | null;
  };
  shouldCreateNewBasketJ2: boolean;
  shouldCreateNewBasketJ1: boolean;
  isBasketOpen: boolean;
  isBasketBooked: boolean;
  isAgencyJourney: boolean;
  agencyInfoPanelVisibility: boolean;
  ui: {
    isMobile: boolean;
  };
  fastbook: {
    ids: string[];
    urnlist: { urn: string; label: string }[];
    isLoading: boolean;
    isExist: boolean;
  };
  digital: number | null;
  industry: string | null;
  duration: number | null;
  startDate: string | null;
  productFilter: SchemaSelectFilter[];
  searchResults: SchemaSearchCompletion[];
  locations: Record<string, SchemaLocationItem>;
  paymentMethods: SchemaPaymentmethods[];
  highlightedLocation: string | null;
  campaignName: string;
  /**
   * These are the current 4 "facet" filter groups you see on the search results:
   */
  attribute: string[]; // e.g. ['analog', 'landscape']
  target: string[]; // e.g. ['bus', 'hotel', 'parking']
  format: string[]; // e.g. ['F200', 'F12']
  commspace: string[]; // e.g. ['street', 'traffic']
  vouchers: { code: string; basket: number }[];
};
export type PartiallyIntegratedUserDataEC = Partial<UserDataEC & { [key: string]: any }>;

export interface AppState {
  availableAttributesJ1: any;
  availableAttributesJ2: SchemaAvailableProductGroupAttributes;
  baskets: any;
  big5Industries: Record<string, SchemaIndustry> | null;
  industriesByKey: Record<string, SchemaIndustry> | null;
  dataPrefilledJ3: boolean;
  campaignDetails: any;
  creationDetails: any;
  errors: { [key: string]: any };
  filters: any;
  industries: SchemaIndustry[] | null;
  filterVisibility: boolean;
  isDrawRadius: boolean;
  keycloakReducer: any;
  loading: Object;
  notices: { [key: string]: any };
  possibleBookingDates: SchemaPossibleBookingDates | null;
  postSelectResponseElements: SchemaProductGroupNode[] | null;
  productionDetails: any;
  productsJ2: SchemaProduct[] | null;
  productsJ2ByUrn: Record<string, SchemaProduct> | null;
  proposals: any;
  proposalsObj: any;
  urlHistory: Array<string>;
  userDataEC: PartiallyIntegratedUserDataEC;
  containers: TypeContainer;
}

(window as any).preload = function () {
  const payload =
    '{"big5Industries":{"150.02":"Alkohol","901.80":"Erotik","960.01":"Politik","950.02":"Religion","185.01":"Tabak"},"filters":null,"industries":{"400.24":"Autohändler / Garagen","105.31":"Brot / Dauerbackwaren","135.02":"Fette / Oele","795.16":"Gewerbliche Beratung","765.16":"Katzenfutter","125.02":"Nährmittel","555.04":"Schmuck","980.01":"Spitäler / Kliniken / Aerzte","140.02":"Suppen / Würzen / Saucen"},"isDrawRadius":true,"loading":false,"possibleBookingDates":{"start":{"kw":35,"year":2020,"date":"2020-08-24T11:44:00+00:00"},"end":{"kw":53,"year":2020,"date":"2021-01-03T11:44:00+00:00"}},"userDataEC":{"basketInfo":null,"basketProducts":{"products":{},"stats":null},"big5Industry":null,"duration":14,"industry":"400.24","startDate":"2020-08-26T22:00:00.000Z","format":[],"target":[],"attribute":[],"locations":[{"city":{"address":"Zermatt, Switzerland","lngLtd":{"lat":46.0207133,"lng":7.749117000000001}},"circle":{"lngLtd":{"lat":46.0207133,"lng":7.749117000000001},"radius":10000}}],"location":{"city":{"address":"Zermatt, Switzerland","lngLtd":{"lat":46.0207133,"lng":7.749117000000001}},"circle":{"lngLtd":{"lat":46.0207133,"lng":7.749117000000001},"radius":10000}}},"productsJ2":null}';

  sessionStorage.setItem('userDataEC', payload);
};

const initialUserDataEC: PartiallyIntegratedUserDataEC = {
  clientHash: null,
  attribute: [],
  audience: null,
  basketInfo: null,
  lastBookedBasketInfo: null,
  basketProducts: {
    products: [],
    productsById: {},
    productsByUrn: {},
    productsByUrnDate: {}, // Neu
    productsByUrnAll: {}, // Neu
    stats: null,
  },
  big5Industry: null,
  budget: null,
  campaigns: null,
  commspace: [],
  dateAccuracy: null,
  details: {},
  digital: null,
  duration: null,
  format: [],
  industry: null,
  invalidProductsJ3: null,
  locations: {},
  startDate: null,
  selectedBasketProposal: null,
  target: [],
  userInfo: null,
  vouchers: [],
  chosenProposal: {
    basket: null,
  },
  mapCenter: {
    lat: 47.3768866,
    lng: 8.541694,
  },
  shouldCreateNewBasketJ2: true,
  shouldCreateNewBasketJ1: false,
  isBasketOpen: false,
  searchResults: [],
  highlightedLocation: null,
  digiflex: {
    product: null,
    startDate: null,
    timeslot: 1,
    timeslotInfo: null,
    spotLength: 10,
    dailyTransmission: 0,
    totalTransmission: 0,
    dailyRepetitions: 0,
    shareOfTime: 0,
    isProductInBasket: false,
    invalidDaysInRange: [],
    dateRange: [],
    duration: 7,
    budget: 0,
    initData: {},
    isLoading: false,
  },
  fastbook: {
    ids: [],
    urnlist: [],
    isLoading: false,
    isExist: false,
  },
  ui: {
    isMobile: false,
  },
  productFilters: [],
  campaignName: '',
  isBasketBooked: false,
  isAgencyJourney: false,
  agencyInfoPanelVisibility: false,
  agencyData: {},
  basketData: null,
};

const getInitialState = () => {
  let lsUserData = {};
  let ecState = {};

  try {
    lsUserData = JSON.parse((window as any).sessionStorage.getItem('userDataEC'));
    ecState = JSON.parse((window as any).sessionStorage.getItem('ecState'));
    if (lsUserData && lsUserData['clientHash']) {
      (window as any).clientHash = lsUserData['clientHash'];
    } else {
      (window as any).clientHash = uuidv4();
    }

    if (lsUserData && (window as any).commitHash !== lsUserData['commitHash']) {
      lsUserData = null;
      ecState = null;
      (window as any).sessionStorage.removeItem('userDataEC');
      (window as any).sessionStorage.removeItem('ecState');
    }
  } catch (error) {
    lsUserData = null;
    ecState = null;
    console.warn('There was a problem with LS', error);
  }

  return {
    baskets: ecState ? ecState['baskets'] : null,
    dataPrefilledJ3: false,
    urlHistory: ecState ? ecState['urlHistory'] || [] : [],
    campaignDetails: null,
    creationDetails: null,
    errors: {},
    notices: {},
    productionDetails: null,
    proposals: ecState ? ecState['proposals'] : null,
    proposalsObj: ecState ? ecState['proposalsObj'] : null,
    big5Industries: null,
    industriesByKey: {},
    filters: null,
    product: null,
    prevProduct: null,
    nextProduct: null,
    industries: null,
    proposalsError: null,
    isDrawRadius: true,
    filterVisibility: true,
    keycloakReducer: {
      ready: (window as any).keycloakReadyDjango || false,
      authenticated: false,
      tokens: null,
    },
    loading: {
      general: false,
      userinfo: false,
      calendar: false,
      basket: false,
      basketReserving: false,
      priceForProduct: false,
      productsPost: false,
    },
    possibleBookingDates: null,
    userDataEC: {
      ...initialUserDataEC,
      ...lsUserData,
    },
    postSelectResponseElements: null,
    productsJ2: null,
    productsJ2ByUrn: {},
    availableAttributesJ2: null,
    availableAttributesJ1: null,
    customersForAgency: null,
    selectedCustomer: null,
    containers: {
      type: Types.journey1,
      isJourney1: window.location.href.includes('journey1'),
      isJourney2: window.location.href.includes('journey2'),
      isJourney3: window.location.href.includes('journey3'),
      url: '',
    },
  };
};

type AppActionType =
  | AgencyActionType
  | SujetActionType
  | BasketActionType
  | AppUtilityActionType
  | ProductActionType
  | LocationsActionType
  | EcomSchemaActionType
  | ResultFiltersActionType
  | CheckoutFastbookActionType
  | LoadingErrorNoticeActionType
  | Journey1LegacyActionType;

const app = (state: AppState = getInitialState(), action: AppActionType) => {
  switch (action.type) {
    case constants.OPEN_BASKET:
    case constants.CLOSE_BASKET:
    case constants.SET_IS_BASKET_BOOKED:
    case constants.BASKET_DATA_POSTED:
    case constants.RESERVED_BASKET:
    case constants.CREATED_BASKET:
    case constants.BOOKED_BASKET:
    case constants.BASKET_CUSTOMER_POSTED:
    case constants.FETCHED_BASKET_STATE:
    case constants.FETCHED_BASKET_INFO:
    case constants.FETCHED_LAST_BOOKED_BASKET_INFO:
    case constants.CLEAR_BASKET_INFO:
    case constants.SET_BASKET_INFO:
    case constants.ADDED_TO_BASKET:
    case constants.EDIT_IN_BASKET:
    case constants.REMOVED_FROM_BASKET:
    case constants.FETCHED_BASKET_DEFINITION:
    case constants.FETCHED_BASKETJ2:
    case constants.FETCHED_BASKET:
    case constants.BASKET_SELECTION_UPDATED:
    case constants.CHOOSE_BASKET:
    case constants.UPDATE_DATE_DURATION:
    case constants.COPIED_BASKET_J3:
    case constants.ADDED_VOUCHER:
    case constants.DELETED_VOUCHER:
      return basketReducer(state, action, initialUserDataEC);

    case constants.POST_SELECT:
    case constants.FETCHED_PRODUCT:
    case constants.CHANGED_PRODUCT_DATE:
    case constants.FETCHED_PRODUCT_PRICE:
    case constants.FETCHED_DIGITAL_PRODUCT_AVAILABILITY:
    case constants.FETCHED_PRODUCT_FILTERS:
      return productReducer(state, action);

    case constants.CLEAR_LOCATIONS:
    case constants.UPDATE_CITY_LOCATION:
    case constants.ADD_LOCATION:
    case constants.UPDATE_LOCATION:
    case constants.FETCH_LOCATION_POLYGONAL_BORDER:
    case constants.REMOVE_LOCATION:
    case constants.UPDATE_MAP_CENTER:
    case constants.HIGHLIGHT_LOCATION:
    case constants.UPDATE_LOCATION_J3:
    case constants.TOGGLE_DRAW_RADIUS:
      return locationsReducer(state, action, initialUserDataEC);

    case constants.LOADING:
    case constants.POST_ERROR:
    case constants.FETCH_ERROR:
    case constants.NOTICE_RECEIVED:
    case constants.CLEARED_ERROR:
    case constants.CLEARED_NOTICE:
      return loadingErrorNoticeReducer(state, action);

    case constants.SET_AGENCY_INFO_PANEL_VISIBILITY:
    case constants.SET_IS_AGENCY_MODE:
    case constants.SET_AGENCY_DATA:
    case constants.FETCHED_CUSTOMERS:
    case constants.SELECT_CUSTOMER:
    case constants.FETCHED_USER_INFO:
      return agencyReducer(state, action);

    // Before any search took place:
    // Industries, Possible Bookingdates, Filters.. the schema before any user action took place:
    case constants.FETCHED_INDUSTRIES_BIG5:
    case constants.FETCHED_BOOKING_DATES:
    case constants.FETCHED_FILTERS:
    case constants.FETCHED_INDUSTRIES:
    case constants.UPDATE_INDUSTRY_AND_CLEAN_BASKET:
    case constants.UPDATE_INDUSTRY:
      return ecommerceSchemaReducer(state, action);

    // After search filters:
    case constants.SET_RESULTS_FILTERS_VISIBILITY:
    case constants.SEARCH_RESULT:
    case constants.UPDATE_DATE_ACCURACY:
    case constants.SET_RESULTS_FILTERS:
    case constants.UPDATE_DURATION:
    case constants.UPDATE_DIGITAL:
    case constants.UPDATE_BUDGET:
    case constants.UPDATE_START_DATE:
    case constants.UPDATE_ATTRIBUTES1:
    case constants.UPDATE_ATTRIBUTES2:
    case constants.RESET_ATTRIBUTES2:
    case constants.SET_FILTERS:
      return resultFilterReducer(state, action);

    case constants.LOCATION_CHANGE:
    case constants.CLEAR_DATA:
    case constants.SET_UI_DATA:
    case constants.PREFILL_DATA:
    case constants.KEYCLOAK_STATE:
    case constants.SET_TYPE_CONTAINER:
      return appUtilitySchemaReducer(state, action, () => getInitialState());

    case constants.GET_ORDER_COMPANY:
    case constants.GET_CREATION_ORDER:
    case constants.GET_PRODUCTION_ORDER:
    case constants.GET_CAMPAIGNS:
    case constants.FETCHED_CAMPAIGN:
      return sujetReducer(state, action);

    case constants.STEP1_SAVE:
    case constants.FETCHED_PROPOSALS:
    case constants.SET_CHOSEN_PROPOSAL:
    case constants.POSSIBLE_FORMATS:
    case constants.FETCHED_BUDGETS:
      return journey1LegacyReducer(state, action);

    case constants.PAYMENT_EVENT:
    case constants.FETCHED_PAYMENT_METHODS:
    case constants.FETCHED_SHARE_OF_TIME:
    case constants.SET_FASTBOOK_DATA:
    case constants.SET_DIGIFLEX_DATA:
    case constants.CLEAR_DIGIFLEX_DATA:
    case constants.SET_CAMPAIGN_NAME:
      return checkoutFastbookReducer(state, action);

    default:
      return state;
  }
};

export default app;
